import { DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { Drawer, Scrollbar } from '@repo/go-ui';
import type { GridColDef, GridColumnVisibilityModel } from '@repo/go-table';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { MIN_VISIBLE_COLUMNS } from '../alert-rules-table/services/DisplayManager';
import { DisplaySettingItem } from '../components/DisplaySettingItem';

import { AUTO_ANOMALIES_SERVICE_COLUMNS } from './AutoAnomaliesDisplayManager';
import type { AutoAnomaliesDisplayManager } from './AutoAnomaliesDisplayManager';
import { FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY } from './FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY';

const HIDDEN_IN_SETTINGS = [...AUTO_ANOMALIES_SERVICE_COLUMNS, '__check__'];

type Props = {
  displayManager: AutoAnomaliesDisplayManager;
  columns: GridColDef[];
  open: boolean;
  onClose: () => void;
  container?: HTMLDivElement;
};

export const AutoAnomaliesTableSettings = observer<Props>(function AutoAnomaliesTableSettings({
  displayManager,
  columns,
  open,
  onClose,
  container,
}) {
  const { t } = useTranslation();

  const { leftPinned, free, rightPinned, orderedColumns } = useMemo(() => {
    const { left, right } = displayManager.settings.pinnedColumns ?? {};

    const { leftPinned, free, rightPinned } = columns.reduce(
      (
        acc: {
          leftPinned: GridColDef[];
          free: GridColDef[];
          rightPinned: GridColDef[];
        },
        col,
      ) => {
        if (left?.includes(col.field)) {
          acc.leftPinned.push(col);
          return acc;
        }
        if (right?.includes(col.field)) {
          acc.rightPinned.push(col);
          return acc;
        }
        acc.free.push(col);
        return acc;
      },
      { leftPinned: [], free: [], rightPinned: [] },
    );

    const columnsOrderModel = displayManager.settings.columnsOrderModel;
    if (columnsOrderModel?.length) {
      free.sort((a, b) => {
        const c1 = columnsOrderModel.indexOf(a.field);
        const c2 = columnsOrderModel.indexOf(b.field);
        if (c1 < 0 && c2 < 0) return 0;
        if (c1 < 0) return -1;
        if (c2 < 0) return 1;
        return c1 - c2;
      });
    }

    return { leftPinned, free, rightPinned, orderedColumns: [...leftPinned, ...free, ...rightPinned] };
  }, [columns, displayManager.settings.columnsOrderModel, displayManager.settings.pinnedColumns]);

  const handleColumnVisibilityChange = (column: string, value: boolean) => {
    const { settings } = displayManager;
    const newVisibilityModel: GridColumnVisibilityModel = {
      ...settings.columnsVisibility,
      [column]: value,
    };
    displayManager.onColumnVisibilityChange(newVisibilityModel);
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { settings } = displayManager;
      const savedColumnsOrderModel = settings?.columnsOrderModel ?? [];
      const columnsOrderModel = [...columns.map((c) => c.field)].sort((a, b) => {
        const c1 = savedColumnsOrderModel.indexOf(a);
        const c2 = savedColumnsOrderModel.indexOf(b);
        if (c1 < 0 && c2 < 0) return 0;
        if (c1 < 0) return -1;
        if (c2 < 0) return 1;
        return c1 - c2;
      });

      const activeField = event.active.id;
      const overField = event.over?.id;
      if (overField == null || activeField == null) return;

      const newIndex = columnsOrderModel.findIndex((item) => item === overField);
      const oldIndex = columnsOrderModel.findIndex((item) => item === activeField);
      const reordered = arrayMove(columnsOrderModel, oldIndex, newIndex);
      displayManager.onColumnsOrderModelChange(reordered);
    },
    [columns, displayManager],
  );

  const checkIsColumnVisibilityDisabled = (columnField: string): boolean => {
    const { settings } = displayManager;
    if (!settings.columnsOrderModel) return false;

    const visibleColumns = settings.columnsVisibility;
    if (!visibleColumns) return false;

    const isColumnVisible = visibleColumns[columnField] === true || visibleColumns[columnField] === undefined;
    let visibleColumnsCount = 0;
    settings.columnsOrderModel.forEach((item) => {
      if (visibleColumns[item] === true || visibleColumns[item] === undefined) {
        visibleColumnsCount += 1;
      }
    });

    const columnDef = columns.find((column) => column.field === columnField);
    if (!columnDef) return true;
    if (columnDef.hideable === false) return true;
    if (!isColumnVisible) return false;
    return visibleColumnsCount <= MIN_VISIBLE_COLUMNS;
  };

  const renderItems = (renderedColumns: GridColDef[]): ReactNode => {
    return renderedColumns.map((column) => {
      if (HIDDEN_IN_SETTINGS.includes(column.field)) {
        return null;
      }

      const titleKey = FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY[column.field] ?? column.field;

      return (
        <DisplaySettingItem
          key={column.field}
          field={column.field}
          title={t(titleKey)}
          isVisibilitySwitcherDisabled={checkIsColumnVisibilityDisabled(column.field)}
          isVisible={
            displayManager.settings.columnsVisibility?.[column.field] === undefined
              ? true
              : !!displayManager.settings.columnsVisibility[column.field]
          }
          onColumnVisibilityChange={handleColumnVisibilityChange}
        />
      );
    });
  };

  return (
    <Drawer
      open={open}
      onClose={() => onClose()}
      style={{ width: 320 }}
      title={t('alertsManagerWidget:autoAnomalies.tableSettingsTitle')}
      container={container}
    >
      <Scrollbar>
        {renderItems(leftPinned)}
        <DndContext modifiers={[restrictToVerticalAxis, restrictToParentElement]} onDragEnd={handleDragEnd}>
          <SortableContext items={orderedColumns.map((col) => col.field)}>
            <div>{renderItems(free)}</div>
          </SortableContext>
        </DndContext>
        {renderItems(rightPinned)}
      </Scrollbar>
    </Drawer>
  );
});
